<template>
    <div>
        <v-tooltip top>
            <template v-slot:activator="{ on: tooltip }">
                <v-btn @click="isOpen = true" icon v-on="{ tooltip }">
                    <v-icon class="mb-3"> fas fa-brain </v-icon>
                </v-btn>
            </template>
            Générer avec l'IA
        </v-tooltip>


        <div v-if="isOpen" class="chatbot-wrapper">
            <v-card class="chatbot-card">
                <v-toolbar dense flat>
                    <v-toolbar-title class="title primary--text d-flex">
                        Assistant IA

                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <v-icon class="ml-4" color="blue" small v-on="on">fas fa-info-circle</v-icon>
                            </template>

                            <div>
                                Pour obtenir une réponse client plus adaptée, vous pouvez formuler des
                                consignes précises à l'IA. Par exemple :

                                <ul>
                                    <li> “Rester concis” </li>
                                    <li> “Employer un ton rassurant et professionnel” </li>
                                    <li> “Répondre de façon rassurante sur l'avancement du chantier” </li>
                                    <li> “Détailler simplement les prochaines étapes du projet” </li>
                                    <li> “Confirmer la bonne prise en compte de la demande du client” </li>
                                </ul>
                            </div>
                        </v-tooltip>
                    </v-toolbar-title>

                    <v-spacer />

                    <v-tooltip top>
                        <template v-slot:activator="{ on }">
                            <v-btn @click="close()" color="primary" icon v-on="on">
                                <v-icon> far fa-times-circle </v-icon>
                            </v-btn>
                        </template>
                        Fermer la fenêtre
                    </v-tooltip>
                </v-toolbar>

                <v-divider />

                <v-card-text class="px-4 d-flex flex-column fill-height">
                    <div class="flex-grow-1">
                        <div class="chat-window" ref="chatWindow" style="overflow-y: auto; max-height: 300px;">
                            <div v-for="(msg, index) in history" :key="index" class="message-row"
                                :class="msg.type === 'user' ? 'me' : 'other'">
                                <v-card class="pa-3 message-bubble rounded"
                                    :color="msg.type === 'user' ? 'indigo lighten-4' : 'grey lighten-3'" flat tile>
                                    <span class="msg-text">{{ msg.content }}</span>
                                </v-card>
                            </div>
                        </div>
                    </div>

                    <div class="flex-grow-0">
                        <v-chip v-if="history.length > 1" color="teal" dark @click="submit()">
                            Choisir cette réponse
                        </v-chip>

                        <v-text-field v-model="userInput" @keypress.enter="generate()" class="mt-2 mb-4" autofocus solo
                            dense placeholder="Quelle est votre demande?">
                            <template v-slot:append>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-btn @click="generate()" class="pb-2" icon v-on="on">
                                            <v-icon> fas fa-paper-plane </v-icon>
                                        </v-btn>
                                    </template>
                                    Envoyer
                                </v-tooltip>
                            </template>
                        </v-text-field>
                    </div>
                </v-card-text>
            </v-card>
        </div>
    </div>
</template>

<script>
export default {
    name: 'OpenAIMessageAssistant',

    props: {
        conversationMessages: { type: Array, required: true }
    },

    data: () => ({
        isOpen: false,
        history: [],
        userInput: '',
        previousResponseId: null
    }),

    methods: {
        close() {
            this.isOpen = false;
        },

        reset() {
            this.close();
            this.history = [];
            this.userInput = '';
            this.previousResponseId = null;
        },

        async submit() {
            const lastMessage = this.history[this.history.length - 1];
            this.$emit('submit', lastMessage.content);
            this.isOpen = false;
        },

        async generate() {
            if (this.previousResponseId !== null) {
                await this.followUpConversation();
            } else {
                await this.initiateConversation();
            }

            this.$nextTick(() => {
                const el = this.$refs.chatWindow;
                el.scrollTop = el.scrollHeight;
            });
        },

        async initiateConversation() {
            try {
                this.setLoading(true);

                this.history.push({
                    type: 'user',
                    content: this.userInput
                });

                const { response, responseId } = await this.repos.openai.messagingInitiateConversation({
                    messages: this.conversationMessages,
                    userInput: this.userInput
                });
                this.history.push({
                    type: 'ai',
                    content: response
                });
                this.previousResponseId = responseId;
                this.userInput = '';
            } catch (err) {
                console.error(err);
                this.$notify({
                    title: 'Erreur',
                    text: 'Une erreur est survenue lors de l\'envoi du message',
                    type: 'error'
                });
            } finally {
                this.setLoading(false);
            }
        },

        async followUpConversation() {
            try {
                this.setLoading(true);

                this.history.push({
                    type: 'user',
                    content: this.userInput
                });

                const { response, responseId } = await this.repos.openai.messagingFollowUpConversation({
                    previousResponseId: this.previousResponseId,
                    userInput: this.userInput
                });
                this.history.push({
                    type: 'ai',
                    content: response
                });
                this.previousResponseId = responseId;
                this.userInput = '';
            } catch (err) {
                console.error(err);
                this.$notify({
                    title: 'Erreur',
                    text: 'Une erreur est survenue lors de l\'envoi du message',
                    type: 'error'
                });
            } finally {
                this.setLoading(false);
            }
        }
    }
};
</script>

<style scoped>
/* Position the chatbot at bottom-left */
.chatbot-wrapper {
    position: fixed;
    bottom: 130px;
    right: 16px;
    z-index: 2000;
    /* above most page content */
}

/* Size of the chatbot card */
.chatbot-card {
    width: 420px;
    max-width: 90vw;
    height: 480px;
}

/* Scrollable messages area */
.chatbot-messages {
    overflow-y: auto;
}

/* Simple slide-up animation */
.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.2s ease-out;
}

.slide-up-enter,
.slide-up-leave-to {
    opacity: 0;
    transform: translateY(10px);
}




.chat-window {
    max-height: 300px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

.message-row {
    display: flex;
    margin: 8px 0;
}

.message-row.other {
    justify-content: flex-start;
}

.message-row.me {
    justify-content: flex-end;
}

.message-bubble {
    max-width: 60%;
    word-wrap: break-word;
}

.msg-text {
    white-space: pre-wrap;
}
</style>
